import { InputWithTypeahead } from "@/overlay/components/WeatherParameterSearchForm/WeatherParamPicker";
import { useSearchEngineReady } from "@/weather-parameters";
import Logger from "@mm/logging";
import { type ExactMatchResult, computeTypeahead } from "@mm/weather-parameter-utils";
import type { SearchResults } from "@mm/weather-parameter-utils";
import type { WeatherParamSearchParallel } from "@mm/weather-parameter-utils/search-engines/parallel";
import { useCallback, useEffect, useState } from "react";

const logger = Logger.fromFilename(__filename);

export function BasicWeatherParameterSearchInput(props: {
  /**
   * Make sure to always pass the same instance of search engine if the search engine is the same.
   * Otherwise, the life cycle hook will trigger the search term refreshing.
   */
  searchEngine: WeatherParamSearchParallel;
  onChange: (result: SearchResults) => void;
  /**
   * A callback for when the user typed in an exact string of weather parameter and hit enter.
   * E.g) t_2m:C
   */
  onSelectExactParamMatch: (result: ExactMatchResult) => void;
}) {
  const searchEngineReady = useSearchEngineReady();
  const [searchterm, setSearchterm] = useState("");
  const [searchResults, setSearchResults] = useState<null | SearchResults>(null);
  const [typeahead, setTypeahead] = useState("");
  const onChange = props.onChange;

  const updateSearchtermAndResults = useCallback(
    (newSearchterm: string) => {
      setSearchterm(newSearchterm);

      props.searchEngine
        .search(newSearchterm)
        .then((response) => {
          setSearchResults(response.results);
          if (response.results) {
            onChange(response.results);
          }
          // TODO: progress typeahead and invalidate if the prefix no longer matches instead
          // of only updating if a new search result arrives.
          let newTypeahead = "";

          if (response.results) {
            newTypeahead = computeTypeahead(response.results.regular) ?? "";
          }

          setTypeahead(newTypeahead);
        })
        .catch((e) => {
          // TODO: errors here are most likely reoccuring. We should probably show an error in this case instead of logging.
          // However, any error here renders the application unusable... So, adding CI tests to prevent this is most likely
          // a better option than a nice error message.
          logger.error(`search for term probably close to ${searchterm} errored out.`, e);
        });
    },
    [props.searchEngine, searchterm, onChange],
  );

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Enter" && searchResults && searchResults.regular[0]) {
      updateSearchtermAndResults("");
      // props.onChange(searchResults);
      props.onSelectExactParamMatch(searchResults.regular[0]);
    }
  }

  useEffect(() => {
    // Refresh the search results when the search engine changed.
    updateSearchtermAndResults(searchterm);
  }, [props.searchEngine, searchterm, updateSearchtermAndResults]);

  if (!searchEngineReady) {
    return <></>;
  }

  return (
    <div className="WeatherParameterSearch">
      <InputWithTypeahead
        autoFocus={true}
        value={searchterm}
        typeahead={typeahead}
        onKeyDown={(e) => handleKeyDown(e)}
        onChange={(e) => updateSearchtermAndResults(e.target.value)}
      />
    </div>
  );
}
